import { styled, Tooltip } from '@mui/material'
import { ArrowSquareOutIcon, WarningIcon, XIcon } from '@phosphor-icons/react'
import { TinyNavButton } from 'ComponentLibrary/src/piazza/buttons'
import { ContentChip } from 'ComponentLibrary/src/piazza/content'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { type CSSProperties } from 'react'
import {
    findReferenceChoice,
    getReferenceChoiceLabel,
    useReferenceChoices,
    useReferenceEditHref,
    type TextReference,
} from './referenceModel'

export function ReferenceChip({
    reference,
    raw,
    onDelete,
    onResolve,
    focusable = true,
    textStyle,
}: {
    reference: TextReference
    raw: string
    onDelete?: () => void
    onResolve?: () => void
    focusable?: boolean
    textStyle?: CSSProperties
}) {
    const choices = useReferenceChoices() ?? []
    const getEditHref = useReferenceEditHref()
    const resolvedEditHref = getEditHref?.(reference)
    const choice = findReferenceChoice(reference, choices)
    const canResolveTarget =
        !!onResolve &&
        !choice &&
        choices.some((candidate) => candidate.field_id === reference.field_id && candidate.target?.kind === 'structure')
    const label = choice ? getReferenceChoiceLabel(choice) : 'Unbekannte Referenz'
    const value = choice?.available && choice.display_value !== null ? choice.display_value : `${label} nicht verfügbar`
    const unavailable = !choice?.available || choice.display_value === null
    const visibleValue = choice ? value : raw
    const tooltip = choice ? label : `Unbekannte Referenz: ${raw}`
    const editLinkLabel = choice ? `Zum ${label}` : tooltip
    const accessibilityLabel = choice ? `${visibleValue}; Referenz ${label}` : `${visibleValue}; Referenz ${label}: ${raw}`
    const deleteLabel = choice ? `Referenz ${label} löschen` : `Referenz ${raw} löschen`

    return (
        <Tooltip
            title={
                resolvedEditHref ? (
                    <TinyNavButton
                        href={resolvedEditHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        ariaLabel={`${editLinkLabel} (öffnet neuen Tab)`}
                        label={editLinkLabel}
                        rightIcon={<ArrowSquareOutIcon aria-hidden="true" />}
                    />
                ) : (
                    tooltip
                )
            }
            arrow
            describeChild
            slotProps={{
                tooltip: {
                    sx: {
                        backgroundColor: tokens.ColorSurfaceDefault,
                        boxShadow: tokens.Flyout.boxShadow,
                        color: tokens.ColorTextDefault,
                        padding: resolvedEditHref ? tokens.SizingSpacing025 : tokens.SizingSpacing100,
                        maxWidth: resolvedEditHref ? 'none' : undefined,
                        '& .MuiTooltip-arrow': { color: tokens.ColorSurfaceDefault },
                    },
                },
            }}
        >
            <ChipContainer
                $unavailable={unavailable}
                $resolvable={canResolveTarget}
                contentEditable={false}
                aria-label={accessibilityLabel}
                data-reference-token={raw}
                role="group"
                tabIndex={focusable ? 0 : undefined}
                onPointerDown={
                    canResolveTarget
                        ? (event) => {
                              if (
                                  event.button === 0 &&
                                  !(event.target instanceof Element && event.target.closest('[data-reference-delete]'))
                              ) {
                                  event.preventDefault()
                              }
                          }
                        : undefined
                }
                onClick={
                    canResolveTarget
                        ? (event) => {
                              event.stopPropagation()
                              onResolve?.()
                          }
                        : undefined
                }
            >
                <ContentChip
                    size="small"
                    color={unavailable ? 'red' : 'blue'}
                    icon={unavailable ? <WarningIcon aria-hidden="true" /> : undefined}
                    label={textStyle ? <span style={textStyle}>{visibleValue}</span> : visibleValue}
                />
                {onDelete && (
                    <DeleteButton
                        type="button"
                        data-reference-delete
                        aria-label={deleteLabel}
                        onMouseDown={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                        }}
                        onKeyDownCapture={(event) => event.stopPropagation()}
                        onClick={(event) => {
                            event.stopPropagation()
                            onDelete()
                        }}
                    >
                        <XIcon size={tokens.SizingIconS} weight="bold" aria-hidden="true" />
                    </DeleteButton>
                )}
            </ChipContainer>
        </Tooltip>
    )
}

const ChipContainer = styled('span', { shouldForwardProp: (prop) => prop !== '$unavailable' && prop !== '$resolvable' })<{
    $unavailable: boolean
    $resolvable: boolean
}>(({ $unavailable, $resolvable }) => ({
    alignItems: 'center',
    backgroundColor: $unavailable ? tokens.ColorSurfaceAccentRedDefault : tokens.ColorSurfaceAccentBlueDefault,
    borderRadius: tokens.SizingCornerS,
    cursor: $resolvable ? 'pointer' : undefined,
    display: 'inline-flex',
    gap: tokens.SizingSpacing025,
    margin: `0 ${tokens.SizingSpacing025}`,
    paddingRight: tokens.SizingSpacing050,
    verticalAlign: 'middle',
    '&:focus-visible': {
        outline: `${tokens.SizingBorderM} solid ${tokens.ColorBorderFocused}`,
    },
}))

const DeleteButton = styled('button')(() => ({
    alignItems: 'center',
    appearance: 'none',
    background: 'transparent',
    border: 0,
    borderRadius: tokens.SizingCornerS,
    color: tokens.ColorIconDefault,
    cursor: 'pointer',
    display: 'inline-flex',
    flexShrink: 0,
    height: tokens.SizingIconS,
    justifyContent: 'center',
    padding: 0,
    width: tokens.SizingIconS,
    '&:focus-visible': { outline: `${tokens.SizingBorderM} solid ${tokens.ColorBorderFocused}` },
    '&:hover': { backgroundColor: tokens.ColorSurfaceHover },
    '&:active': { backgroundColor: tokens.ColorSurfacePressed },
}))
